import React, { useState, useEffect } from 'react';
import "../../css/Reports.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FaChartBar, FaSearch, FaTimes, FaFilter, FaDownload, FaPrint,
    FaCalendarAlt, FaUserGraduate, FaChalkboardTeacher, FaEye,
    FaFilePdf, FaFileExcel, FaFileAlt, FaUserFriends
} from 'react-icons/fa';
import Loader from "../../components/Loader";

function Reports() {
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterPeriod, setFilterPeriod] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    // Report types
    const reportTypes = [
        { id: 'student', name: 'Student' },
        { id: 'faculty', name: 'Faculty' },
        { id: 'class', name: 'Class' },
        { id: 'department', name: 'Department' },
        { id: 'financial', name: 'Financial' },
        { id: 'attendance', name: 'Attendance' }
    ];

    // Report categories
    const reportCategories = [
        { id: 'academic', name: 'Academic Performance' },
        { id: 'behavioral', name: 'Behavioral' },
        { id: 'attendance', name: 'Attendance' },
        { id: 'exam', name: 'Examination Results' },
        { id: 'performance', name: 'Faculty Performance' },
        { id: 'feedback', name: 'Feedback' },
        { id: 'financial', name: 'Financial' }
    ];

    // Time periods
    const timePeriods = [
        { id: 'daily', name: 'Daily' },
        { id: 'weekly', name: 'Weekly' },
        { id: 'monthly', name: 'Monthly' },
        { id: 'quarterly', name: 'Quarterly' },
        { id: 'semester', name: 'Semester' },
        { id: 'annual', name: 'Annual' }
    ];

    // Fetch reports
    const fetchReports = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }
        
        try {
            // Placeholder for API call - replace with actual endpoint
            // const response = await fetch("https://schoolpro-api.uniums.com/api/reports", {
            //     headers: {
            //         "Authorization": `Bearer ${token}`
            //     }
            // });
            // if (!response.ok) {
            //     const errorData = await response.json();
            //     throw new Error(errorData.message || "Failed to fetch reports");
            // }
            // const data = await response.json();
            // setReports(data);
            
            // Mock data for demonstration
            setTimeout(() => {
                const mockReports = generateMockReports();
                setReports(mockReports);
                setIsLoading(false);
            }, 1000);
        } catch (error) {
            console.error("Error fetching reports:", error);
            toast.error(`Error: ${error.message}`);
            setIsLoading(false);
        }
    };

    // Generate mock reports data
    const generateMockReports = () => {
        const mockData = [];
        const reportTitles = {
            student: [
                "Semester Performance Report", 
                "Student Attendance Report", 
                "Academic Progress Report",
                "Behavioral Assessment Report"
            ],
            faculty: [
                "Teaching Effectiveness Report",
                "Faculty Research Output",
                "Peer Evaluation Report",
                "Faculty Attendance Summary"
            ],
            class: [
                "Class Performance Analysis",
                "Class Attendance Overview",
                "Subject-wise Performance Report"
            ],
            department: [
                "Department Achievement Summary",
                "Departmental Staff Performance",
                "Department Resource Utilization"
            ],
            financial: [
                "Fee Collection Status",
                "Budget Allocation Summary",
                "Scholarship Distribution Report",
                "Financial Year Summary"
            ],
            attendance: [
                "Daily Attendance Record",
                "Monthly Attendance Summary",
                "Attendance Trend Analysis",
                "Absence Pattern Report"
            ]
        };
        
        const reportTypes = ['student', 'faculty', 'class', 'department', 'financial', 'attendance'];
        const categories = ['academic', 'behavioral', 'attendance', 'exam', 'performance', 'feedback', 'financial'];
        const periods = ['daily', 'weekly', 'monthly', 'quarterly', 'semester', 'annual'];
        const statuses = ['completed', 'pending', 'draft'];
        
        for (let i = 1; i <= 20; i++) {
            const type = reportTypes[Math.floor(Math.random() * reportTypes.length)];
            const category = categories[Math.floor(Math.random() * categories.length)];
            const period = periods[Math.floor(Math.random() * periods.length)];
            const status = statuses[Math.floor(Math.random() * statuses.length)];
            const titles = reportTitles[type];
            const title = titles[Math.floor(Math.random() * titles.length)];
            
            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(endDate.getDate() - (Math.floor(Math.random() * 30) + 1));
            
            mockData.push({
                _id: `report_${i}`,
                title,
                description: `This report provides detailed information about ${type} ${category} data collected over the ${period} period.`,
                type,
                category,
                period,
                status,
                createdAt: new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000).toISOString(),
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                generatedBy: `Admin ${Math.floor(Math.random() * 10) + 1}`,
                reportUrl: i % 3 === 0 ? `/reports/report_${i}.pdf` : null,
                dataPoints: Math.floor(Math.random() * 1000) + 50
            });
        }
        
        return mockData;
    };

    useEffect(() => {
        fetchReports();
    }, []);

    // Filter and sort functions
    const getFilteredReports = () => {
        if (!reports.length) return [];

        return reports.filter(report => {
            const matchesSearch = 
                report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                report.description.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesType = 
                filterType === 'all' || report.type === filterType;
            
            const matchesCategory = 
                filterCategory === 'all' || report.category === filterCategory;
                
            const matchesPeriod = 
                filterPeriod === 'all' || report.period === filterPeriod;
            
            return matchesSearch && matchesType && matchesCategory && matchesPeriod;
        });
    };

    const getSortedReports = () => {
        const filteredData = getFilteredReports();
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            if (sortConfig.key === 'createdAt' || sortConfig.key === 'startDate' || sortConfig.key === 'endDate') {
                const dateA = new Date(a[sortConfig.key]);
                const dateB = new Date(b[sortConfig.key]);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            return sortConfig.direction === 'asc'
                ? a[sortConfig.key].localeCompare(b[sortConfig.key])
                : b[sortConfig.key].localeCompare(a[sortConfig.key]);
        });
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Helper functions
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getCategoryLabel = (categoryId) => {
        const category = reportCategories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    const getTypeLabel = (typeId) => {
        const type = reportTypes.find(t => t.id === typeId);
        return type ? type.name : 'Unknown';
    };

    const getPeriodLabel = (periodId) => {
        const period = timePeriods.find(p => p.id === periodId);
        return period ? period.name : 'Unknown';
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed': return 'adminReports-status-completed';
            case 'pending': return 'adminReports-status-pending';
            case 'draft': return 'adminReports-status-draft';
            default: return 'adminReports-status-pending';
        }
    };

    const getTypeIconClass = (type) => {
        switch (type) {
            case 'student': return 'adminReports-type-student';
            case 'faculty': return 'adminReports-type-faculty';
            case 'class': return 'adminReports-type-class';
            case 'department': return 'adminReports-type-department';
            case 'financial': return 'adminReports-type-financial';
            case 'attendance': return 'adminReports-type-attendance';
            default: return 'adminReports-type-other';
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'student': return <FaUserGraduate />;
            case 'faculty': return <FaChalkboardTeacher />;
            case 'class': return <FaUserFriends />;
            case 'department': return <FaChartBar />;
            case 'financial': return <FaFileExcel />;
            case 'attendance': return <FaCalendarAlt />;
            default: return <FaFileAlt />;
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterType('all');
        setFilterCategory('all');
        setFilterPeriod('all');
        setSortConfig({ key: 'createdAt', direction: 'desc' });
    };

    // Modal handler
    const openViewModal = (report) => {
        setSelectedReport(report);
        setShowViewModal(true);
    };

    return (
        <div className="adminReports-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            <div className="adminReports-header">
                <div className="adminReports-title-container">
                    <FaChartBar className="adminReports-title-icon" />
                    <h1 className="adminReports-title">Administrative Reports</h1>
                </div>

                <div className="adminReports-stats">
                    <div className="adminReports-stat-box">
                        <div className="adminReports-stat-icon adminReports-stat-icon-total">
                            <FaFileAlt />
                        </div>
                        <div className="adminReports-stat-info">
                            <span className="adminReports-stat-number">{reports.length}</span>
                            <span className="adminReports-stat-label">Total Reports</span>
                        </div>
                    </div>
                    <div className="adminReports-stat-box">
                        <div className="adminReports-stat-icon adminReports-stat-icon-student">
                            <FaUserGraduate />
                        </div>
                        <div className="adminReports-stat-info">
                            <span className="adminReports-stat-number">
                                {reports.filter(report => report.type === 'student').length}
                            </span>
                            <span className="adminReports-stat-label">Student Reports</span>
                        </div>
                    </div>
                    <div className="adminReports-stat-box">
                        <div className="adminReports-stat-icon adminReports-stat-icon-faculty">
                            <FaChalkboardTeacher />
                        </div>
                        <div className="adminReports-stat-info">
                            <span className="adminReports-stat-number">
                                {reports.filter(report => report.type === 'faculty').length}
                            </span>
                            <span className="adminReports-stat-label">Faculty Reports</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="adminReports-filters">
                <div className="adminReports-filter-row">
                    <div className="adminReports-filter-group">
                        <label className="adminReports-filter-label">Report Type</label>
                        <select
                            className="adminReports-filter-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            {reportTypes.map(type => (
                                <option key={type.id} value={type.id}>
                                    {type.name} Reports
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="adminReports-filter-group">
                        <label className="adminReports-filter-label">Report Category</label>
                        <select
                            className="adminReports-filter-select"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {reportCategories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="adminReports-filter-group">
                        <label className="adminReports-filter-label">Time Period</label>
                        <select
                            className="adminReports-filter-select"
                            value={filterPeriod}
                            onChange={(e) => setFilterPeriod(e.target.value)}
                        >
                            <option value="all">All Periods</option>
                            {timePeriods.map(period => (
                                <option key={period.id} value={period.id}>
                                    {period.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="adminReports-search-container">
                    <FaSearch className="adminReports-search-icon" />
                    <input
                        type="text"
                        className="adminReports-search-input"
                        placeholder="Search reports by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="adminReports-search-clear" onClick={() => setSearchTerm('')}>
                            <FaTimes />
                        </button>
                    )}
                    <button className="adminReports-btn adminReports-btn-clear-filters" onClick={clearFilters}>
                        <FaTimes /> Clear Filters
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="adminReports-loading">
                    <Loader />
                    <p>Loading reports...</p>
                </div>
            ) : (
                <div className="adminReports-content">
                    {reports.length === 0 ? (
                        <div className="adminReports-empty">
                            <FaChartBar className="adminReports-empty-icon" />
                            <h2>No Reports Available</h2>
                            <p>There are currently no reports to display.</p>
                        </div>
                    ) : getSortedReports().length === 0 ? (
                        <div className="adminReports-no-results">
                            <h3>No Matching Reports</h3>
                            <p>No reports match your current search or filter criteria.</p>
                            <button className="adminReports-btn adminReports-btn-secondary" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        </div>
                    ) : (
                        <div className="adminReports-grid">
                            {getSortedReports().map((report) => (
                                <div key={report._id} className={`adminReports-card adminReports-card-${report.status}`}>
                                    <div className="adminReports-card-header">
                                        <div className={`adminReports-card-icon ${getTypeIconClass(report.type)}`}>
                                            {getTypeIcon(report.type)}
                                        </div>
                                        <div className="adminReports-card-title-group">
                                            <h3 className="adminReports-card-title">
                                                {report.title}
                                                {report.reportUrl && (
                                                    <span className="adminReports-file-indicator">
                                                        <FaFilePdf />
                                                    </span>
                                                )}
                                            </h3>
                                            <div className="adminReports-card-meta-top">
                                                <span className={`adminReports-type-badge ${getTypeIconClass(report.type)}`}>
                                                    {getTypeLabel(report.type)}
                                                </span>
                                                <span className={`adminReports-status-badge ${getStatusBadgeClass(report.status)}`}>
                                                    {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="adminReports-card-body">
                                        <div className="adminReports-card-meta">
                                            <span className="adminReports-category-badge">
                                                {getCategoryLabel(report.category)}
                                            </span>
                                            <span className="adminReports-period-badge">
                                                {getPeriodLabel(report.period)}
                                            </span>
                                        </div>

                                        <p className="adminReports-card-description">
                                            {report.description.length > 120
                                                ? `${report.description.substring(0, 120)}...`
                                                : report.description}
                                        </p>

                                        <div className="adminReports-card-details">
                                            <div className="adminReports-card-detail">
                                                <FaCalendarAlt /> 
                                                <span>{formatDate(report.startDate)} - {formatDate(report.endDate)}</span>
                                            </div>
                                            <div className="adminReports-card-detail">
                                                <FaFileAlt /> 
                                                <span>{report.dataPoints} data points</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="adminReports-card-footer">
                                        <div className="adminReports-card-created">
                                            Created: {formatDate(report.createdAt)}
                                        </div>
                                        <div className="adminReports-card-actions">
                                            {report.reportUrl && (
                                                <a
                                                    href={report.reportUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="adminReports-btn adminReports-btn-download"
                                                >
                                                    <FaDownload />
                                                </a>
                                            )}
                                            <button 
                                                className="adminReports-btn adminReports-btn-print"
                                                onClick={() => console.log(`Print report ${report._id}`)}
                                            >
                                                <FaPrint />
                                            </button>
                                            <button 
                                                className="adminReports-btn adminReports-btn-view"
                                                onClick={() => openViewModal(report)}
                                            >
                                                <FaEye />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* View Report Modal */}
            {showViewModal && selectedReport && (
                <div className="adminReports-modal-overlay">
                    <div className="adminReports-modal adminReports-view-modal">
                        <div className="adminReports-modal-header">
                            <div className="adminReports-modal-title-container">
                                <div className={`adminReports-modal-icon ${getTypeIconClass(selectedReport.type)}`}>
                                    {getTypeIcon(selectedReport.type)}
                                </div>
                                <h2 className="adminReports-modal-title">Report Details</h2>
                            </div>
                            <button
                                className="adminReports-modal-close"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        
                        <div className="adminReports-modal-content">
                            <div className="adminReports-view-content">
                                <div className="adminReports-view-header">
                                    <h3 className="adminReports-view-title">{selectedReport.title}</h3>
                                    <div className="adminReports-view-meta">
                                        <span className={`adminReports-type-badge ${getTypeIconClass(selectedReport.type)}`}>
                                            {getTypeLabel(selectedReport.type)} Report
                                        </span>
                                        <span className={`adminReports-status-badge ${getStatusBadgeClass(selectedReport.status)}`}>
                                            {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                                        </span>
                                        <span className="adminReports-category-badge">
                                            {getCategoryLabel(selectedReport.category)}
                                        </span>
                                        <span className="adminReports-period-badge">
                                            {getPeriodLabel(selectedReport.period)}
                                        </span>
                                    </div>
                                </div>

                                <div className="adminReports-view-details">
                                    <div className="adminReports-view-detail">
                                        <span className="adminReports-view-label"><FaCalendarAlt /> Period Covered:</span>
                                        <span className="adminReports-view-value">
                                            {formatDate(selectedReport.startDate)} to {formatDate(selectedReport.endDate)}
                                        </span>
                                    </div>
                                    <div className="adminReports-view-detail">
                                        <span className="adminReports-view-label"><FaCalendarAlt /> Report Generated:</span>
                                        <span className="adminReports-view-value">{formatDate(selectedReport.createdAt)}</span>
                                    </div>
                                    <div className="adminReports-view-detail">
                                        <span className="adminReports-view-label"><FaUserGraduate /> Generated By:</span>
                                        <span className="adminReports-view-value">{selectedReport.generatedBy}</span>
                                    </div>
                                    <div className="adminReports-view-detail">
                                        <span className="adminReports-view-label"><FaFileAlt /> Data Points:</span>
                                        <span className="adminReports-view-value">{selectedReport.dataPoints}</span>
                                    </div>
                                </div>

                                <div className="adminReports-view-body">
                                    <h4 className="adminReports-view-section-title">Report Description</h4>
                                    <div className="adminReports-view-text">
                                        {selectedReport.description}
                                    </div>
                                </div>

                                <div className="adminReports-view-chart">
                                    <h4 className="adminReports-view-section-title">Report Summary</h4>
                                    <div className="adminReports-chart-placeholder">
                                        {/* Placeholder for actual chart */}
                                        <div className="adminReports-chart-bars">
                                            <div className="adminReports-chart-bar" style={{ height: '65%' }}></div>
                                            <div className="adminReports-chart-bar" style={{ height: '80%' }}></div>
                                            <div className="adminReports-chart-bar" style={{ height: '45%' }}></div>
                                            <div className="adminReports-chart-bar" style={{ height: '90%' }}></div>
                                            <div className="adminReports-chart-bar" style={{ height: '75%' }}></div>
                                            <div className="adminReports-chart-bar" style={{ height: '60%' }}></div>
                                        </div>
                                        <p className="adminReports-chart-note">Preview chart - View full report for detailed visualizations</p>
                                    </div>
                                </div>

                                {selectedReport.reportUrl && (
                                    <div className="adminReports-view-download">
                                        <h4 className="adminReports-view-section-title">Full Report</h4>
                                        <a
                                            href={selectedReport.reportUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="adminReports-download-link"
                                        >
                                            <FaFilePdf className="adminReports-download-icon" />
                                            <span>Download Complete Report (PDF)</span>
                                            <FaDownload className="adminReports-action-icon" />
                                        </a>
                                    </div>
                                )}

                                <div className="adminReports-view-actions">
                                    <button 
                                        className="adminReports-btn adminReports-btn-print-full"
                                        onClick={() => console.log(`Print report ${selectedReport._id}`)}
                                    >
                                        <FaPrint /> Print Report
                                    </button>
                                    <button
                                        className="adminReports-btn adminReports-btn-primary"
                                        onClick={() => setShowViewModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Reports;